import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeToggle } from '../../_shared/ThemeToggle';
import { CommonUsagePanel } from '../../_shared/CommonUsagePanel';
import { useLogout } from '../hooks/useLogout';
import { useLogoutAll } from '../hooks/useLogoutAll';
import { useMe } from '../../_shared/hooks/useMe';
import type { CommonUsageItem } from '../../_shared/CommonUsagePanel';

// ─── Common Usage Items (§6.7.1 — 8 directly-related) ────────────────────────

const COMMON_USAGE_ITEMS: CommonUsageItem[] = [
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'ApiResponse<Void> (C002)',
    role: '표준 logout 응답 wrapper',
    snippet: 'return ApiResponse.success(null);',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'ErrorResponse (C002)',
    role: '4xx/5xx 오류 본문 (FE 매핑)',
    snippet: 'if (!res.success) showToast(res.error.code);',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'GlobalExceptionHandler (C001)',
    role: '컨트롤러 advice (자동 적용)',
    snippet: '// 자동 적용 — biz 코드 없음',
  },
  {
    module: 'eraf-core',
    moduleColor: 'blue',
    api: 'RequestContextHolder.current() (C006)',
    role: 'logout 액터 userId/traceId MDC 조회',
    snippet: 'Long actor = RequestContextHolder.current().userId();',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'JwtAuthenticationService.revoke(at, rt) (C028)',
    role: '표준 logout 핵심 위임 — 단일 세션 폐기',
    snippet: 'jwtAuthenticationService.revoke(accessToken, refreshToken);',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'JwtAuthenticationService.revokeAllForUser(userId) (C028)',
    role: '다른 기기 전체 폐기 (revokeAll=true)',
    snippet: 'jwtAuthenticationService.revokeAllForUser(claims.userId());',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'JwtTokenBlacklist (Redis + JPA dual store) (C028)',
    role: 'logout 시 jti blacklist 등록',
    snippet: 'jwtBlacklist.add(jti, expiresAt); // 자동',
  },
  {
    module: 'eraf-security',
    moduleColor: 'yellow',
    api: 'SecurityChainCustomizer · PathPolicy (C029)',
    role: '/logout authenticated 경로 보호',
    snippet: 'PathPolicy.authenticated("/api/eraf/auth/logout")',
  },
];

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const id = setTimeout(onClose, 3000);
    return () => clearTimeout(id);
  }, [onClose]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 'var(--spacing-6)',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-text-primary)',
        zIndex: 2000,
        boxShadow: 'var(--shadow-md)',
        maxWidth: '360px',
        textAlign: 'center',
      }}
    >
      {message}
    </div>
  );
}

// ─── ExpiredTokenBanner (R7-036) ──────────────────────────────────────────────

interface ExpiredTokenBannerProps {
  onDismiss: () => void;
}

function ExpiredTokenBanner({ onDismiss }: ExpiredTokenBannerProps) {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{
        background: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))',
        border: '1px solid var(--color-warning)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        marginBottom: 'var(--spacing-4)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-3)',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 'var(--font-size-lg)', flexShrink: 0, lineHeight: 1.4 }}>
        ⚠
      </span>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: 'var(--color-warning)',
            marginBottom: 'var(--spacing-1)',
          }}
        >
          {t('logout.expiredBanner.title')}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('logout.expiredBanner.description')}
        </div>
      </div>
      <button
        type="button"
        aria-label={t('logout.expiredBanner.dismiss')}
        onClick={onDismiss}
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'var(--font-family-sans)',
          padding: '0 var(--spacing-1)',
        }}
      >
        ✕
      </button>
    </div>
  );
}

// ─── RevokeAllConfirmDialog (R7-032: triggered by logout button, not checkbox) ─

interface RevokeAllConfirmDialogProps {
  onConfirm: () => void;
  onCancel: () => void;
}

function RevokeAllConfirmDialog({ onConfirm, onCancel }: RevokeAllConfirmDialogProps) {
  const { t } = useTranslation();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel]
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay-backdrop)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title-revoke-all"
      aria-describedby="dialog-desc-revoke-all"
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: '360px',
          maxWidth: '90vw',
          boxShadow: 'var(--shadow-lg)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          id="dialog-title-revoke-all"
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-warning)',
            marginBottom: 'var(--spacing-4)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          <span aria-hidden="true">⚠</span> {t('logout.dialog.revokeAllTitle')}
        </div>
        <div
          id="dialog-desc-revoke-all"
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.7,
            marginBottom: 'var(--spacing-6)',
          }}
        >
          {t('logout.dialog.revokeAllConfirm')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-3)' }}>
          {/* R7-034: cancel button uses t('logout.button.cancel') — key exists in both ko/en */}
          <button
            type="button"
            aria-label={t('logout.dialog.cancelAriaLabel')}
            onClick={onCancel}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('logout.button.cancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            aria-label={t('logout.dialog.confirmAriaLabel')}
            onClick={onConfirm}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-danger)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('logout.dialog.confirmButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────

/** R7-036: Check whether the stored access token is expired. */
function isAccessTokenExpired(): boolean {
  try {
    const expiresInStr =
      localStorage.getItem('access_expires_in') ??
      sessionStorage.getItem('access_expires_in');
    const accessToken =
      localStorage.getItem('access_token') ??
      sessionStorage.getItem('access_token');

    if (!accessToken) return true; // no token → treat as expired

    // JWT payload decode (no signature verification — display-only)
    const parts = accessToken.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.exp && typeof payload.exp === 'number') {
      return Date.now() / 1000 > payload.exp;
    }

    // Fallback: use stored access_expires_in seconds (relative to login time)
    if (expiresInStr) {
      // access_expires_in is stored as seconds since epoch of issue
      // We use the iat claim if available
      if (payload.iat && typeof payload.iat === 'number') {
        const expiresAt = (payload.iat + Number(expiresInStr)) * 1000;
        return Date.now() > expiresAt;
      }
    }
    return false;
  } catch {
    return false;
  }
}

// ─── LogoutConfirmPage ────────────────────────────────────────────────────────

export function LogoutConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const logoutAllMutation = useLogoutAll();

  // R7-033 fix: revokeAll is a standalone controlled boolean — NOT tied to dialog open
  const [revokeAll, setRevokeAll] = useState(false);
  // R7-032 fix: showDialog is triggered only by the logout submit button, not by checkbox
  const [showDialog, setShowDialog] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // R7-036: expired token banner state
  const [showExpiredBanner, setShowExpiredBanner] = useState(false);

  // Redirect countdown timer ref for 401 errors
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeMutation = revokeAll ? logoutAllMutation : logoutMutation;
  const isPending = activeMutation.isPending;
  const isError = activeMutation.isError;
  const errorCode = activeMutation.error
    ? (activeMutation.error as { code?: string })?.code ?? ''
    : '';
  const isTokenError =
    errorCode === 'AUTH-401-003' || errorCode === 'AUTH-401-004';

  // Fetch current user from server — no client-side JWT decoding
  const { data: meData } = useMe();
  const userEmail = meData?.email ?? '';

  // R7-036: detect expired token on mount and show banner
  useEffect(() => {
    if (isAccessTokenExpired()) {
      setShowExpiredBanner(true);
    }
  }, []);

  // Auto-redirect on token error (401)
  useEffect(() => {
    if (isTokenError) {
      redirectTimerRef.current = setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        navigate('/login', { replace: true });
      }, 3000);
    }
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, [isTokenError, navigate]);

  // R7-032/033 fix: logout button click handler.
  // revokeAll checkbox state is managed independently.
  // Dialog opens only when user explicitly clicks the logout button while revokeAll=true.
  const handleLogoutClick = useCallback(() => {
    if (revokeAll) {
      // Open confirmation dialog — checkbox state is already set
      setShowDialog(true);
    } else {
      // R7-003: use mutateRef so this closure always calls the latest mutate
      logoutMutation.mutateRef.current(undefined, {
        onError: (err) => {
          const code = (err as { code?: string })?.code ?? '';
          if (code !== 'AUTH-401-003' && code !== 'AUTH-401-004') {
            setToast(t('logout.toast.error'));
          }
        },
      });
    }
  }, [revokeAll, logoutMutation, t]);

  const handleDialogConfirm = useCallback(() => {
    setShowDialog(false);
    // R7-003: use mutateRef to avoid stale closure
    logoutAllMutation.mutateRef.current(undefined, {
      onError: (err) => {
        const code = (err as { code?: string })?.code ?? '';
        if (code !== 'AUTH-401-003' && code !== 'AUTH-401-004') {
          setToast(t('logout.toast.error'));
        }
      },
    });
  }, [logoutAllMutation, t]);

  const handleDialogCancel = useCallback(() => {
    setShowDialog(false);
    // R7-033: closing the dialog does NOT reset the checkbox — user retains their choice
  }, []);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleRetry = useCallback(() => {
    logoutMutation.reset();
    logoutAllMutation.reset();
  }, [logoutMutation, logoutAllMutation]);

  // R7-033 fix: toggle is purely independent of dialog state
  const handleRevokeToggle = useCallback(() => {
    setRevokeAll((v) => !v);
  }, []);

  const handleRevokeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setRevokeAll((v) => !v);
      }
    },
    []
  );

  const getErrorMessage = (): string => {
    if (!isError) return '';
    if (isTokenError)
      return t('logout.error.tokenInvalid');
    const code = errorCode;
    if (!code || code.startsWith('HTTP-0') || code === 'Network Error') {
      return t('logout.error.network');
    }
    return (
      (activeMutation.error as { message?: string })?.message ??
      t('logout.toast.error')
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-surface-background)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-8)',
        position: 'relative',
      }}
      aria-busy={isPending}
    >
      {/* Dialog state — R7-032: only opens on logout button click when revokeAll=true */}
      {showDialog && (
        <RevokeAllConfirmDialog
          onConfirm={handleDialogConfirm}
          onCancel={handleDialogCancel}
        />
      )}

      {/* Toast notification */}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      {/* ── Logout Card ─────────────────────────────────────────────────── */}
      <div
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
          padding: 'var(--spacing-8)',
          width: '100%',
          maxWidth: '420px',
          position: 'relative',
        }}
        role="main"
        aria-label={t('logout.confirm.title')}
      >
        {/* Theme toggle */}
        <div
          style={{ position: 'absolute', top: 'var(--spacing-4)', right: 'var(--spacing-4)' }}
        >
          <ThemeToggle />
        </div>

        {/* Logo */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: '40px',
              height: '40px',
              background: 'var(--color-brand-primary)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-inverse)',
              fontWeight: 800,
              fontSize: 'var(--font-size-base)',
              marginRight: 'var(--spacing-2)',
              flexShrink: 0,
            }}
          >
            E
          </div>
          <span
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 800,
              color: 'var(--color-brand-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            eraf
          </span>
        </div>

        {/* Title */}
        <h1
          style={{
            textAlign: 'center',
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            margin: '0 0 var(--spacing-2)',
          }}
        >
          {t('logout.confirm.title')}
        </h1>

        {/* Subtitle — R7-001: uses {{email}} interpolation (i18next standard) */}
        <p
          style={{
            textAlign: 'center',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--spacing-6)',
          }}
        >
          {userEmail
            ? t('logout.subtitle', { email: userEmail })
            : t('logout.confirm.subtitle')}
        </p>

        {/* R7-036: Expired token banner */}
        {showExpiredBanner && (
          <ExpiredTokenBanner onDismiss={() => setShowExpiredBanner(false)} />
        )}

        {/* Error state banner */}
        {isError && (
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            id="logout-error-banner"
            style={{
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-3) var(--spacing-4)',
              marginBottom: 'var(--spacing-4)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--spacing-3)',
            }}
          >
            <span
              aria-hidden="true"
              style={{ fontSize: 'var(--font-size-lg)', flexShrink: 0, lineHeight: 1.4 }}
            >
              ⚠
            </span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--color-danger)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                {t('logout.error.bannerTitle')}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                {getErrorMessage()}
              </div>
              {errorCode && (
                <code
                  style={{
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    background: 'var(--color-surface-elevated)',
                    padding: '1px var(--spacing-1)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  {errorCode}
                </code>
              )}
              {isTokenError && (
                <div
                  style={{
                    marginTop: 'var(--spacing-1)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {t('logout.error.redirecting')}
                </div>
              )}
              {!isTokenError && (
                <button
                  type="button"
                  aria-label={t('logout.error.retry')}
                  onClick={handleRetry}
                  style={{
                    display: 'inline-block',
                    marginTop: 'var(--spacing-2)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    color: 'var(--color-brand-primary)',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('logout.error.retry')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* revokeAll toggle — R7-033: purely controlled, independent of dialog */}
        <div
          role="checkbox"
          aria-checked={revokeAll}
          aria-label={t('logout.revokeAll.checkboxAriaLabel')}
          tabIndex={0}
          onClick={handleRevokeToggle}
          onKeyDown={handleRevokeKeyDown}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-2)',
            marginBottom: 'var(--spacing-6)',
            padding: 'var(--spacing-3)',
            background: 'var(--color-surface-elevated)',
            border: revokeAll
              ? '1px solid var(--color-brand-primary)'
              : '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: '18px',
              height: '18px',
              flexShrink: 0,
              marginTop: '2px',
              border: revokeAll
                ? '1.5px solid var(--color-brand-primary)'
                : '1.5px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              background: revokeAll
                ? 'var(--color-brand-primary)'
                : 'var(--color-surface-elevated)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {revokeAll && (
              <span
                style={{
                  color: 'var(--color-text-inverse)',
                  fontSize: '10px',
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                ✓
              </span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <span
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
              }}
            >
              {t('logout.revokeAll.label')}
            </span>
            <span
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginTop: '2px',
              }}
            >
              {t('logout.revokeAll.description')}
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div
          style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}
        >
          {/* Logout button — R7-032: opens dialog only here, not on checkbox change */}
          <button
            type="button"
            disabled={isPending}
            aria-disabled={isPending}
            aria-label={
              isPending
                ? t('logout.button.loading')
                : revokeAll
                ? t('logout.revokeAll.label')
                : t('logout.button.submit')
            }
            aria-describedby={isError ? 'logout-error-banner' : undefined}
            onClick={handleLogoutClick}
            style={{
              flex: 1,
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-2)',
              opacity: isPending ? 0.6 : 1,
            }}
          >
            {/* Loading spinner */}
            {isPending && (
              <span
                aria-hidden="true"
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid var(--color-border-subtle)',
                  borderTopColor: 'var(--color-text-inverse)',
                  borderRadius: 'var(--radius-full)',
                  display: 'inline-block',
                  animation: 'logout-spin 0.8s linear infinite',
                  flexShrink: 0,
                }}
              />
            )}
            {isPending ? t('logout.button.loading') : t('logout.button.submit')}
          </button>

          {/* Cancel button — R7-034: t('logout.button.cancel') exists in both ko/en */}
          <button
            type="button"
            disabled={isPending}
            aria-label={t('logout.cancel.ariaLabel')}
            onClick={handleCancel}
            style={{
              flex: 1,
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: isPending ? 0.6 : 1,
            }}
          >
            {t('logout.button.cancel')}
          </button>
        </div>
      </div>

      {/* ── §6.7 Common Usage Panel ──────────────────────────────────────── */}
      <div style={{ width: '100%', maxWidth: '960px', marginTop: 'var(--spacing-4)' }}>
        <CommonUsagePanel
          items={COMMON_USAGE_ITEMS}
          defaultOpen={false}
          title={t('logout.commonUsage.title')}
        />
      </div>

      {/* Spinner keyframe */}
      <style>{`
        @keyframes logout-spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 767px) {
          .logout-confirm-card {
            padding: var(--spacing-6) !important;
          }
        }
      `}</style>
    </div>
  );
}
